import React, { useState, useEffect } from 'react';
import api from '../lib/api';

const CAUSE_OPTIONS = [
  { id: 'environment', label: 'Environment & Climate', icon: '🌍', category: 'Environment' },
  { id: 'education', label: 'Education & Youth', icon: '📚', category: 'Education' },
  { id: 'health', label: 'Health & Wellness', icon: '💚', category: 'Health Care' },
  { id: 'hunger', label: 'Hunger & Poverty', icon: '🍽️', category: 'Human Services' },
  { id: 'diversity', label: 'Diversity & Inclusion', icon: '🤝', category: 'Civil Rights' },
  { id: 'community', label: 'Local Community', icon: '🏘️', category: 'Community' },
  { id: 'arts', label: 'Arts & Culture', icon: '🎨', category: 'Arts, Culture & Humanities' },
  { id: 'animals', label: 'Animal Welfare', icon: '🐾', category: 'Animal-Related' },
];

const SDG_OPTIONS = [
  { id: 'sdg1', label: 'No Poverty', number: 1 },
  { id: 'sdg2', label: 'Zero Hunger', number: 2 },
  { id: 'sdg3', label: 'Good Health', number: 3 },
  { id: 'sdg4', label: 'Quality Education', number: 4 },
  { id: 'sdg5', label: 'Gender Equality', number: 5 },
  { id: 'sdg6', label: 'Clean Water', number: 6 },
  { id: 'sdg7', label: 'Clean Energy', number: 7 },
  { id: 'sdg8', label: 'Economic Growth', number: 8 },
  { id: 'sdg10', label: 'Reduced Inequalities', number: 10 },
  { id: 'sdg11', label: 'Sustainable Cities', number: 11 },
  { id: 'sdg12', label: 'Responsible Consumption', number: 12 },
  { id: 'sdg13', label: 'Climate Action', number: 13 },
  { id: 'sdg14', label: 'Life Below Water', number: 14 },
  { id: 'sdg15', label: 'Life on Land', number: 15 },
];

const GEOGRAPHIC_OPTIONS = [
  { id: 'local', label: 'Local (City/Region)' },
  { id: 'state', label: 'State-wide' },
  { id: 'national', label: 'National (USA)' },
  { id: 'international', label: 'International/Global' },
];

const BUDGET_RANGES = [
  { value: 'under_10k', label: 'Under $10,000' },
  { value: '10k_50k', label: '$10,000 - $50,000' },
  { value: '50k_100k', label: '$50,000 - $100,000' },
  { value: '100k_500k', label: '$100,000 - $500,000' },
  { value: '500k_1m', label: '$500,000 - $1M' },
  { value: 'over_1m', label: 'Over $1M' },
  { value: 'not_set', label: 'Not yet determined' },
];

const OnboardingWizard = ({ isOpen, onClose, onComplete, businessId }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    csr_goals: [],
    target_causes: [],
    target_sdgs: [],
    geographic_focus: [],
    csr_budget: '',
    mission_statement: '',
    brand_values: '',
    marketing_priority: 'balanced'
  });

  // Reset form data when wizard opens to ensure clean state
  React.useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFormData({
        csr_goals: [],
        target_causes: [],
        target_sdgs: [],
        geographic_focus: [],
        csr_budget: '',
        mission_statement: '',
        brand_values: '',
        marketing_priority: 'balanced'
      });
    }
  }, [isOpen]);

  const toggleArrayItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.put(
        `/api/businesses/${businessId}`,
        {
          ...formData,
          onboarding_completed: true
        }
      );
      onComplete();
    } catch (error) {
      console.error('Error saving onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api.put(
        `/api/businesses/${businessId}`,
        { onboarding_completed: true }
      );
      onClose();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">Complete Your Profile</h2>
              <p className="text-blue-100 mt-1">Help us personalize your experience</p>
            </div>
            <button 
              onClick={handleSkip}
              className="text-blue-200 hover:text-white text-sm"
            >
              Skip for now
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="flex items-center gap-2 mt-6">
            {[1, 2, 3].map(s => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= s ? 'bg-white text-blue-600' : 'bg-blue-500 text-blue-200'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`flex-1 h-1 ${step > s ? 'bg-white' : 'bg-blue-500'}`}></div>}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-xs text-blue-200 mt-2">
            <span>CSR Focus</span>
            <span>Goals & Budget</span>
            <span>Preferences</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {step === 1 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">What causes matter to your business?</h3>
              <p className="text-sm text-gray-600 mb-4">Select all that align with your company values</p>
              <div className="grid grid-cols-2 gap-3">
                {CAUSE_OPTIONS.map(cause => (
                  <button
                    key={cause.id}
                    onClick={() => toggleArrayItem('target_causes', cause.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.target_causes.includes(cause.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className="text-2xl mr-2">{cause.icon}</span>
                    <span className={`font-medium ${formData.target_causes.includes(cause.id) ? 'text-blue-700' : 'text-gray-700'}`}>
                      {cause.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">UN Sustainable Development Goals</h3>
                <p className="text-sm text-gray-600 mb-4">Which SDGs align with your CSR strategy? (Optional)</p>
                <div className="grid grid-cols-2 gap-2">
                  {SDG_OPTIONS.map(sdg => (
                    <button
                      key={sdg.id}
                      onClick={() => toggleArrayItem('target_sdgs', sdg.id)}
                      className={`p-3 rounded-lg border text-left text-sm transition-all ${
                        formData.target_sdgs.includes(sdg.id)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300 text-gray-700'
                      }`}
                    >
                      <span className="font-semibold">SDG {sdg.number}:</span> {sdg.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Annual CSR Budget</h3>
                <select
                  value={formData.csr_budget}
                  onChange={(e) => setFormData({...formData, csr_budget: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Select budget range (optional)</option>
                  {BUDGET_RANGES.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Geographic Focus</h3>
                <p className="text-sm text-gray-600 mb-4">Where do you want to make an impact?</p>
                <div className="grid grid-cols-2 gap-3">
                  {GEOGRAPHIC_OPTIONS.map(geo => (
                    <button
                      key={geo.id}
                      onClick={() => toggleArrayItem('geographic_focus', geo.id)}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        formData.geographic_focus.includes(geo.id)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300 text-gray-700'
                      }`}
                    >
                      {geo.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Marketing Priority</h3>
                <p className="text-sm text-gray-600 mb-4">How important is PR/marketing value in charity selection?</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'high', label: 'High Priority', desc: 'Maximize visibility' },
                    { value: 'balanced', label: 'Balanced', desc: 'Equal weight' },
                    { value: 'low', label: 'Impact First', desc: 'Focus on outcomes' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFormData({...formData, marketing_priority: opt.value})}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        formData.marketing_priority === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className={`font-semibold ${formData.marketing_priority === opt.value ? 'text-blue-700' : 'text-gray-700'}`}>
                        {opt.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Company Mission (Optional)</h3>
                <textarea
                  value={formData.mission_statement}
                  onChange={(e) => setFormData({...formData, mission_statement: e.target.value})}
                  placeholder="Briefly describe your company's mission or values..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Step {step} of 3
            </div>
            <div className="flex gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Complete Setup'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
